import React, { useState } from 'react';
import { User, UserRole } from '../types';
import { LogOut, Home, Bus, FileText, Settings, Users, CreditCard, Menu, X, Code2, Search, BarChart2, DollarSign, ScanBarcode } from 'lucide-react';

interface LayoutProps {
  user: User;
  onLogout: () => void;
  onNavigate: (view: string) => void;
  currentView: string;
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ user, onLogout, onNavigate, currentView, children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Updated roles to restrict sensitive areas to MASTER_ADMIN only
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN, UserRole.VIEW_ONLY_ADMIN, UserRole.PAYMENT_ADMIN] },
    { id: 'payments', label: 'Payments', icon: DollarSign, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN, UserRole.VIEW_ONLY_ADMIN, UserRole.PAYMENT_ADMIN] },
    { id: 'staff', label: 'Staff Management', icon: Users, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN, UserRole.VIEW_ONLY_ADMIN] },
    { id: 'survey', label: 'New Survey', icon: FileText, roles: [UserRole.REPRESENTATIVE, UserRole.ADMIN, UserRole.MASTER_ADMIN] },
    { id: 'my-submissions', label: 'My Entries', icon: FileText, roles: [UserRole.REPRESENTATIVE] },
    { id: 'card-gen', label: 'ID Card Mgmt', icon: Settings, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN] },
    { id: 'id-card-print', label: 'ID Card Printing', icon: CreditCard, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN, UserRole.VIEW_ONLY_ADMIN] },
    
    { id: 'attendance', label: 'Attendance', icon: ScanBarcode, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN, UserRole.VIEW_ONLY_ADMIN] },
    { id: 'bus-management', label: 'Bus Trip Mgmt', icon: Bus, roles: [UserRole.MASTER_ADMIN, UserRole.BUS_DRIVER, UserRole.VIEW_ONLY_ADMIN] },
    { id: 'admin-users', label: 'Manage Admins', icon: Users, roles: [UserRole.MASTER_ADMIN] },
    { id: 'settings', label: 'Survey Settings', icon: Settings, roles: [UserRole.MASTER_ADMIN] },
  ];

  const handleNavigate = (id: string) => {
    onNavigate(id);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row relative">
      {/* Mobile Header */}
      <div className="md:hidden bg-emerald-800 text-white p-4 flex justify-between items-center sticky top-0 z-40 shadow-md">
        <h1 className="font-bold text-lg truncate pr-2">Malayalee Samajam Forms</h1>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-emerald-800 text-white transform transition-transform duration-300 ease-in-out shadow-xl flex flex-col
        md:relative md:transform-none md:shadow-none
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="p-6 border-b border-emerald-700 hidden md:block">
          <h1 className="text-xl font-bold">Malayalee Samajam Forms 2026</h1>
          <p className="text-xs text-emerald-200 mt-1 opacity-90 leading-relaxed">Admin Portal</p>
          
          <div className="mt-6 pt-4 border-t border-emerald-700/50">
             <p className="text-sm font-semibold">{user.name}</p>
             <span className="text-xs bg-emerald-900 px-2 py-0.5 rounded text-emerald-100">{user.role}</span>
          </div>
        </div>
        
        {/* Mobile Sidebar Header */}
        <div className="p-6 border-b border-emerald-700 md:hidden flex justify-between items-center">
           <div>
             <p className="font-bold">{user.name}</p>
             <span className="text-xs bg-emerald-900 px-2 py-0.5 rounded text-emerald-100">{user.role}</span>
           </div>
           <button onClick={() => setIsMobileMenuOpen(false)}><X size={20} /></button>
        </div>

        <nav className="p-4 space-y-2 overflow-y-auto flex-1">
          {navItems.filter(item => item.roles.includes(user.role)).map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                currentView === item.id ? 'bg-emerald-600 text-white shadow-lg' : 'hover:bg-emerald-700 text-emerald-100'
              }`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-red-600 text-emerald-100 transition-colors mt-8"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </nav>

        {/* Developer Credit Footer (Sidebar) */}
        <div className="p-4 border-t border-emerald-700 bg-emerald-900/50">
          <div className="flex flex-col items-center justify-center text-center opacity-80 hover:opacity-100 transition-opacity">
             <div className="flex items-center space-x-1 mb-1 text-emerald-400">
               <Code2 size={14} />
               <span className="text-[10px] uppercase tracking-wider font-semibold">Developed by</span>
             </div>
             <p className="text-xs font-bold text-white tracking-wide">Shabeeb Kayakkandy</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto h-[calc(100vh-60px)] md:h-screen pt-4">
        {children}
      </main>
    </div>
  );
};

export default Layout;